// ForgeScreen (§4/§5) — the Forge and the host for target-based modes.
// Free play plus Time Attack (90s scoring), Daily Puzzle, Gauntlet (10 rising
// levels) and a solo Battle race (full multiplayer + chat land in §8). Shares
// the 2-slot bench, tray, combine engine and discovery celebration. Lab (a
// 4-slot workbench) is delegated to LabScreen.
import { AD_SLOTS } from "../../config/adSlots";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Screen from "../ui/Screen";
import ElementOrb from "../ui/ElementOrb";
import DiscoveryOverlay from "./DiscoveryOverlay";
import AchievementToast from "../ui/AchievementToast";
import RewardedHint from "./RewardedHint";
import LabScreen from "./LabScreen";
import { useForge, Feedback } from "../../game/useForge";
import { useElementStore } from "../../store/elementStore";
import { levelFromDiscovered, getStreak } from "../../game/stats";
import { share } from "../../game/share";
import { ELEMENT_DEFS } from "../elementDefinitions";
import { playSound } from "../../services/soundService";
import { loadGameState, saveGameState } from "../../services/storageService";
import {
  ModeKind,
  kindFromLabel,
  MODE_META,
  TIME_LIMIT_SEC,
  dailyTarget,
  nextGauntletTarget,
  gauntletLevelForTarget,
  GAUNTLET_TARGETS,
} from "../../game/modes";

interface ModeResult {
  emoji: string;
  title: string;
  body: string;
  replayLabel: string;
  onReplay: () => void;
}

const feedbackStyleFor = (kind: Feedback["kind"]): React.CSSProperties => {
  const map = {
    info: { bg: "rgba(96,165,250,0.12)", color: "#93C5FD", border: "rgba(96,165,250,0.3)" },
    warn: { bg: "rgba(245,183,64,0.12)", color: "#F5B740", border: "rgba(245,183,64,0.3)" },
    good: { bg: "rgba(52,211,153,0.12)", color: "#34D399", border: "rgba(52,211,153,0.3)" },
  }[kind];
  return { margin: "14px auto 0", maxWidth: 340, padding: "10px 14px", borderRadius: 12, textAlign: "center", fontSize: 13, fontWeight: 600, background: map.bg, color: map.color, border: `1px solid ${map.border}` };
};

const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const ForgeScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedKind = kindFromLabel((location.state as any)?.mode);

  // Lab is a distinct workbench.
  const [kind, setKind] = useState<ModeKind>(requestedKind);
  const [target, setTarget] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT_SEC);
  const [score, setScore] = useState(0);
  const [gauntletCleared, setGauntletCleared] = useState(0);
  const [result, setResult] = useState<ModeResult | null>(null);

  const unlocked = useElementStore((s) => s.unlocked);
  const discovered = unlocked.size;
  const level = levelFromDiscovered(discovered);
  const streak = getStreak().streak;

  // (Re)initialise whenever the requested mode changes (new navigation).
  useEffect(() => {
    setKind(requestedKind);
    setResult(null);
    setScore(0);
    setTimeLeft(TIME_LIMIT_SEC);
    const gs = loadGameState();
    const cleared = gs.gauntletProgress ?? 0;
    setGauntletCleared(cleared);
    if (requestedKind === "daily") setTarget(dailyTarget());
    else if (requestedKind === "gauntlet") setTarget(nextGauntletTarget(cleared));
    else if (requestedKind === "battle") setTarget("diamond");
    else setTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedKind, (location.state as any)?.mode]);

  const onWin = useRef<(id: string) => void>(() => {});
  const forge = useForge({
    mode: kind,
    target,
    onScore: (_gain, total) => setScore(total),
    onWin: (id) => onWin.current(id),
  });

  // Win handling (kept in a ref so useForge's callback always sees fresh state).
  onWin.current = (resultId: string) => {
    playSound("highscore");
    if (kind === "daily") {
      setResult({
        emoji: "🎯",
        title: "Daily solved!",
        body: `You forged ${ELEMENT_DEFS[resultId].name}. Come back tomorrow for a fresh puzzle.`,
        replayLabel: "Keep forging",
        onReplay: () => setResult(null),
      });
    } else if (kind === "gauntlet") {
      const lvl = gauntletLevelForTarget(resultId);
      let cleared = gauntletCleared;
      if (lvl !== -1 && lvl >= gauntletCleared) {
        cleared = lvl + 1;
        setGauntletCleared(cleared);
        saveGameState({ gauntletProgress: cleared });
      }
      const done = cleared >= GAUNTLET_TARGETS.length;
      setResult({
        emoji: done ? "🏆" : "⚔️",
        title: done ? "Gauntlet complete!" : "Level cleared!",
        body: done
          ? "You conquered all 10 gauntlet levels. Masterful."
          : `Level ${cleared} down. Next: forge ${ELEMENT_DEFS[nextGauntletTarget(cleared)].name}.`,
        replayLabel: done ? "Done" : "Next level",
        onReplay: () => {
          if (done) { navigate("/"); return; }
          setTarget(nextGauntletTarget(cleared));
          setResult(null);
        },
      });
    } else if (kind === "battle") {
      setResult({
        emoji: "🏆",
        title: "You win!",
        body: `You forged ${ELEMENT_DEFS[resultId].name} first.`,
        replayLabel: "Rematch",
        onReplay: () => setResult(null),
      });
    }
  };

  // Time Attack countdown.
  useEffect(() => {
    if (kind !== "time" || result) return;
    if (timeLeft <= 0) {
      const gs = loadGameState();
      const best = Math.max(gs.timeAttackHighScore ?? 0, score);
      if (best > (gs.timeAttackHighScore ?? 0)) saveGameState({ timeAttackHighScore: best });
      playSound("gameover");
      setResult({
        emoji: "⏱️",
        title: "Time's up!",
        body: `You scored ${score}. Best: ${best}.`,
        replayLabel: "Play again",
        onReplay: () => { setScore(0); setTimeLeft(TIME_LIMIT_SEC); setResult(null); },
      });
      return;
    }
    const t = setInterval(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [kind, timeLeft, result, score]);

  if (kind === "lab") return <LabScreen />;

  const exitMode = () => navigate("/");

  const onShare = async () => {
    if (!forge.discovery) return;
    await share(`I just discovered ${ELEMENT_DEFS[forge.discovery.id].name} in MystiCrafter! ⚗️`);
  };

  const meta = MODE_META[kind];
  const targetDef = target ? ELEMENT_DEFS[target] : null;

  const goalText =
    kind === "daily" ? `Forge today's target` :
    kind === "gauntlet" ? `Level ${Math.min(gauntletCleared + 1, 10)}: forge the target` :
    kind === "time" ? "Discover as many as you can" :
    kind === "battle" ? "Race to forge the target" : "";
  const statValue =
    kind === "time" ? fmtTime(Math.max(0, timeLeft)) :
    kind === "gauntlet" ? `Lv ${Math.min(gauntletCleared + 1, 10)}/10` :
    kind === "battle" ? "1v1" : "";
  const statLabel = kind === "time" ? `Score ${score}` : kind === "gauntlet" ? "Gauntlet" : kind === "battle" ? "Solo" : "";

  const slotStyle = (filled: boolean): React.CSSProperties => ({
    width: 84, height: 84, borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flex: "0 0 auto",
    background: filled ? "rgba(101,163,13,0.1)" : "var(--fill)",
    border: filled ? "2px solid rgba(101,163,13,0.55)" : "2px dashed var(--track)",
  });

  const renderSlot = (index: 0 | 1) => {
    const id = forge.slots[index];
    return (
      <button
        className="mc-tap"
        onClick={() => id && forge.clearSlot(index)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const d = e.dataTransfer.getData("text/plain"); if (d) forge.placeInSlot(d, index); }}
        style={slotStyle(!!id)}
        aria-label={id ? `Remove ${ELEMENT_DEFS[id]?.name}` : `Empty slot ${index + 1}`}
      >
        {id ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ animation: "mcPop .3s ease", borderRadius: "50%" }}><ElementOrb id={id} size={52} /></div>
            <span style={{ fontSize: 11, color: "var(--text)", fontWeight: 600 }}>{ELEMENT_DEFS[id]?.name}</span>
          </div>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5B5687" strokeWidth={2} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
        )}
      </button>
    );
  };

  const modeActive = kind !== "free";

  const overlay = (
    <>
      <DiscoveryOverlay elementId={forge.discovery?.id ?? null} ingredients={forge.discovery?.ingredients} praise={forge.praise} onShare={onShare} onClose={forge.closeDiscovery} />
      <AchievementToast achievement={forge.achievement} onClose={forge.clearAchievement} />
      {result && (
        <div onClick={() => setResult(null)} style={{ position: "absolute", inset: 0, zIndex: 41, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "rgba(8,9,20,0.86)", backdropFilter: "blur(6px)" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 330, padding: "28px 24px", borderRadius: 24, textAlign: "center", background: "linear-gradient(160deg,var(--hero-1),var(--hero-2))", border: `1px solid ${meta.accent}55`, boxShadow: "0 30px 80px rgba(0,0,0,0.5)", animation: "mcPop .4s ease" }}>
            <div style={{ fontSize: 46 }}>{result.emoji}</div>
            <div style={{ marginTop: 8, fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 700 }}>{result.title}</div>
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>{result.body}</div>
            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <button className="mc-tap" onClick={exitMode} style={{ flex: 1, minHeight: 48, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 14, background: "var(--border)", color: "var(--text-2)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Done</button>
              <button className="mc-tap" onClick={result.onReplay} style={{ flex: 1, minHeight: 48, border: "none", borderRadius: 14, background: "#65A30D", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{result.replayLabel}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <Screen title={meta.label} active="forge" showBack streak={streak} level={level} overlay={overlay} ad={AD_SLOTS.gameplay}>
      {/* Mode banner */}
      {modeActive && (
        <div style={{ padding: "14px 16px", background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            {targetDef ? <ElementOrb id={target!} size={44} withRing /> : <div style={{ width: 44 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: meta.accent }}>{meta.label}</div>
              <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700 }}>
                {goalText}{targetDef ? `: ${targetDef.name}` : ""}
              </div>
            </div>
            {statValue && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 17, fontWeight: 700, color: meta.accent }}>{statValue}</div>
                <div style={{ fontSize: 10, color: "var(--text-3)" }}>{statLabel}</div>
              </div>
            )}
            <button className="mc-tap" onClick={exitMode} aria-label="Exit mode" style={{ width: 32, height: 32, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 10, background: "var(--fill)", color: "var(--text-2)", cursor: "pointer" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
          {kind === "battle" && (
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-3)" }}>
              Solo race for now — live opponents & chat arrive in the Battle update.
            </div>
          )}
        </div>
      )}

      {/* Bench */}
      <div style={{ position: "relative", padding: "20px 16px 18px", background: "radial-gradient(120% 90% at 50% 0%, var(--hero-1) 0%, var(--hero-2) 55%, var(--bg) 100%)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ textAlign: "center", fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--accent-text)" }}>Combine two elements</div>
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
          {renderSlot(0)}
          <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 26, fontWeight: 700, color: "var(--text-3)" }}>+</div>
          {renderSlot(1)}
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "center" }}>
          <button className="mc-tap" onClick={forge.combine} style={{ minHeight: 48, padding: "0 28px", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: forge.canCombine ? "pointer" : "not-allowed", background: forge.canCombine ? "#65A30D" : "var(--fill)", color: forge.canCombine ? "#fff" : "var(--text-3)", boxShadow: forge.canCombine ? "0 8px 20px rgba(101,163,13,0.35)" : "none" }}>Combine</button>
          <button className="mc-tap" onClick={forge.clearSlots} aria-label="Clear slots" style={{ minHeight: 48, width: 48, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, background: "var(--fill)", color: "var(--text-2)", cursor: "pointer" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
          </button>
        </div>
        {forge.feedback && <div style={feedbackStyleFor(forge.feedback.kind)}>{forge.feedback.text}</div>}
        {forge.praise && (
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 16, color: forge.praise.color }}>{forge.praise.word}</span>
            {forge.praise.mult > 1 && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color: "#fff", background: forge.praise.color }}>×{forge.praise.mult}</span>}
          </div>
        )}

        {/* Watch-ad-for-a-recipe (hidden until rewarded ads are enabled) */}
        <RewardedHint />
      </div>

      {/* Tray */}
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, height: 40, padding: "0 12px", borderRadius: 12, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
            <input value={forge.query} onChange={(e) => forge.setQuery(e.target.value)} placeholder="Search your elements…" aria-label="Search your elements" style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13, minWidth: 0 }} />
          </div>
          <span style={{ fontSize: 12, color: "var(--text-3)", whiteSpace: "nowrap" }}>{forge.tray.length} elements</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(74px,1fr))", gap: 12 }}>
          {forge.tray.map((id) => (
            <button key={id} className="mc-tap" draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", id)} onClick={() => forge.pickTray(id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, padding: "10px 4px", borderRadius: 16, background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer" }}>
              <ElementOrb id={id} size={44} />
              <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{ELEMENT_DEFS[id]?.name}</span>
            </button>
          ))}
        </div>
      </div>
    </Screen>
  );
};

export default ForgeScreen;
